import { useState, useEffect, useRef } from 'react';
import { fetchWithRetry } from '../utils/tourUtils';
import { API_BASE_URL } from '../config';
import { FUNCTION_NAMES, BUCKET_NAME_PHOTOS } from '../constants';
import { useToast } from '../components/ui/use-toast';

export const useMeasurements = (selectedTour, rooms, currentRoomId, pannellumRef) => {
  const [measureMode, setMeasureMode] = useState(false);
  const [selectedPoints, setSelectedPoints] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [measurements, setMeasurements] = useState({});
  const [npyFiles, setNpyFiles] = useState(new Set());
  const activeHotspots = useRef(new Set());
  const { toast } = useToast();

  // Fetch NPY files when tour changes
  useEffect(() => {
    if (selectedTour?.id) {
      fetchNpyFiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTour?.id]);

  // Clear measurements and hotspots when changing rooms
  useEffect(() => {
    if (measureMode) {
      clearMeasurementHotspots();
      setSelectedPoints([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRoomId]);

  // Fetch all NPY files from S3
  const fetchNpyFiles = async () => {
    try {
      if (!selectedTour?.id) {
        console.log('No tour selected, skipping NPY fetch');
        return;
      }

      console.log('Fetching NPY files from S3...');
      const response = await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.POST.LIST_S3_KEYS,
          payload: {
            bucketName: BUCKET_NAME_PHOTOS,
            prefixPath: `npy/${selectedTour.id}`,
          },
        },
        label: 'fetching NPY files',
        throwError: true,
      });

      console.log('NPY files response:', response);
      const npySet = new Set(response.map((item) => item.Key));
      setNpyFiles(npySet);
      console.log(`Found ${npySet.size} NPY files:`, Array.from(npySet));
    } catch (error) {
      console.error('Error fetching NPY files:', error);
    }
  };

  // Check if NPY exists for a room
  const checkNpyExists = (room) => {
    if (!room?.imageKey || !selectedTour?.id) return false;
    const imageName = room.imageKey.split('/').pop().replace('.jpg', '');
    const npyKey = `npy/${selectedTour.id}/${imageName}.npy`;
    const exists = npyFiles.has(npyKey);
    console.log(`Checking NPY for room ${room.id}:`, {
      imageKey: room.imageKey,
      imageName,
      npyKey,
      exists,
      totalNpyFiles: npyFiles.size,
    });
    return exists;
  };

  // Get current room's NPY S3 key
  const getCurrentRoomNpyKey = () => {
    const currentRoom = rooms.find(r => r.id === currentRoomId);
    if (!currentRoom?.imageKey || !selectedTour?.id) return null;
    
    const imageName = currentRoom.imageKey.split('/').pop().replace('.jpg', '');
    return `npy/${selectedTour.id}/${imageName}.npy`;
  };

  // Check if current room has both image and NPY data
  const canMeasureCurrentRoom = () => {
    const currentRoom = rooms.find(r => r.id === currentRoomId);
    return currentRoom && checkNpyExists(currentRoom);
  };

  // Function to add a point hotspot for measurements
  const addPointHotspot = (pitch, yaw, id, text) => {
    const viewer = pannellumRef.current?.getViewer();
    if (!viewer) return;

    viewer.addHotSpot({
      pitch: pitch,
      yaw: yaw,
      type: 'info',
      text: text,
      id: id,
      cssClass: 'custom-measurement-hotspot',
      tooltip: `Pitch: ${pitch.toFixed(2)}°, Yaw: ${yaw.toFixed(2)}°`,
    });
    activeHotspots.current.add(id);
  };

  // Function to add a distance hotspot for measurements
  const addDistanceHotspot = (pitch1, yaw1, pitch2, yaw2, distance) => {
    const viewer = pannellumRef.current?.getViewer();
    if (!viewer) return;

    const midPitch = (pitch1 + pitch2) / 2;
    const midYaw = (yaw1 + yaw2) / 2;

    const hotspotId = `distance-line-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    viewer.addHotSpot({
      pitch: midPitch,
      yaw: midYaw,
      type: 'info',
      text: `${distance} cm`,
      id: hotspotId,
      cssClass: 'distance-measurement-hotspot',
      tooltip: 'Distance between points',
    });
    activeHotspots.current.add(hotspotId);
  };

  // Function to clear measurement hotspots
  const clearMeasurementHotspots = () => {
    const viewer = pannellumRef.current?.getViewer();
    if (!viewer) return;

    activeHotspots.current.forEach((id) => {
      try {
        viewer.removeHotSpot(id);
      } catch (error) {
        console.warn(`Failed to remove hotspot ${id}:`, error);
      }
    });
    activeHotspots.current.clear();
  };

  // Handle point selection for measurements
  const handlePointSelection = async (event) => {
    const viewer = pannellumRef.current?.getViewer();
    if (!viewer) return;

    // Prevent default context menu on right click
    if (event.type === 'contextmenu') {
      event.preventDefault();
    }

    const coords = viewer.mouseEventToCoords(event);
    const pitch = coords[0];
    const yaw = coords[1];

    setIsProcessing(true);
    try {
      const npyKey = getCurrentRoomNpyKey();
      if (!npyKey) {
        throw new Error('No depth data available for current room');
      }

      const depthData = await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        retries: 1,
        data: {
          function_name: FUNCTION_NAMES.POST.GET_DEPTH_COORDINATES,
          payload: {
            npy_s3_key: npyKey,
            pitch,
            yaw,
          },
        },
      });

      if (!depthData.success) {
        throw new Error('Failed to get depth data');
      }

      const { x, y, depth } = depthData;
      const newPoint = { x, y, depth, pitch, yaw };

      if (selectedPoints.length === 0) {
        // First point
        setSelectedPoints([newPoint]);
        addPointHotspot(pitch, yaw, 'measurement-point-1', 'Point 1');

        toast({
          title: 'First point selected',
          description: `Yaw: ${yaw.toFixed(2)}°, Pitch: ${pitch.toFixed(2)}°`,
          duration: 3000,
        });
      } else {
        // Second point
        const point1 = selectedPoints[0];
        const point2 = newPoint;

        addPointHotspot(pitch, yaw, 'measurement-point-2', 'Point 2');

        // Convert to 3D point on sphere
        const toRad = (deg) => (deg * Math.PI) / 180;
        const sphToXYZ = (pt) => {
          const theta = toRad(pt.yaw);
          const phi = toRad(90 - pt.pitch);
          return [
            pt.depth * Math.sin(phi) * Math.sin(theta),
            pt.depth * Math.cos(phi),
            pt.depth * Math.sin(phi) * Math.cos(theta),
          ];
        };

        const a = sphToXYZ(point1);
        const b = sphToXYZ(point2);
        const distance = Math.sqrt(
          (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2
        );

        const measurement = {
          id: Date.now(),
          point1: {
            pitch: point1.pitch,
            yaw: point1.yaw,
            depth: point1.depth,
            x: point1.x,
            y: point1.y,
          },
          point2: {
            pitch: point2.pitch,
            yaw: point2.yaw,
            depth: point2.depth,
            x: point2.x,
            y: point2.y,
          },
          distance: parseFloat((distance * 100).toFixed(1)),
        };

        addDistanceHotspot(
          point1.pitch,
          point1.yaw,
          point2.pitch,
          point2.yaw,
          measurement.distance
        );

        setMeasurements((prev) => ({
          ...prev,
          [currentRoomId]: [...(prev[currentRoomId] || []), measurement],
        }));

        toast({
          title: 'Measurement Complete',
          description: `Distance: ${(distance * 100).toFixed(1)} cm`,
          duration: 4000,
        });

        setSelectedPoints([]);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        duration: 3000,
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Setup event listeners for measurement mode
  useEffect(() => {
    if (!pannellumRef.current || !measureMode || !canMeasureCurrentRoom()) return;

    const viewer = pannellumRef.current.getViewer();
    if (!viewer) return;

    const handleContextMenu = (event) => {
      if (measureMode) {
        handlePointSelection(event);
      }
    };

    const handleKeyDown = (event) => {
      if (measureMode && event.code === 'Space') {
        event.preventDefault();
        const pitch = viewer.getPitch();
        const yaw = viewer.getYaw();

        const syntheticEvent = {
          type: 'keyboard',
          preventDefault: () => {},
          target: viewer.getContainer(),
        };

        viewer.mouseEventToCoords = () => [pitch, yaw];
        handlePointSelection(syntheticEvent);
      }
    };

    viewer.getContainer().addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      viewer.getContainer().removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measureMode, selectedPoints, currentRoomId, npyFiles]);

  // Toggle measurement mode
  const toggleMeasurementMode = () => {
    if (!canMeasureCurrentRoom()) {
      toast({
        title: 'Cannot measure',
        description: 'Both image and depth data are required for measurements',
        variant: 'destructive',
      });
      return;
    }

    setMeasureMode(!measureMode);
    setSelectedPoints([]);
    clearMeasurementHotspots();
  };

  // Delete measurement
  const deleteMeasurement = (roomId, measurementId) => {
    // First, clear all measurement hotspots from the viewer
    clearMeasurementHotspots();
    
    // Remove the measurement from state
    setMeasurements((prev) => {
      const updatedMeasurements = {
        ...prev,
        [roomId]: prev[roomId]?.filter((m) => m.id !== measurementId) || [],
      };
      
      // Re-add hotspots for remaining measurements in the current room
      setTimeout(() => {
        const remainingMeasurements = updatedMeasurements[currentRoomId] || [];
        remainingMeasurements.forEach((measurement, index) => {
          // Add point hotspots for remaining measurements
          addPointHotspot(
            measurement.point1.pitch, 
            measurement.point1.yaw, 
            `measurement-point1-${measurement.id}`, 
            `Point 1 (${index + 1})`
          );
          addPointHotspot(
            measurement.point2.pitch, 
            measurement.point2.yaw, 
            `measurement-point2-${measurement.id}`, 
            `Point 2 (${index + 1})`
          );
          // Add distance hotspot for remaining measurements
          addDistanceHotspot(
            measurement.point1.pitch,
            measurement.point1.yaw,
            measurement.point2.pitch,
            measurement.point2.yaw,
            measurement.distance
          );
        });
      }, 100); // Small delay to ensure DOM updates are complete
      
      return updatedMeasurements;
    });
  };

  return {
    measureMode,
    selectedPoints,
    isProcessing,
    measurements,
    npyFiles,
    canMeasureCurrentRoom,
    toggleMeasurementMode,
    deleteMeasurement,
    clearMeasurementHotspots,
  };
};
